/**
 * ActivityBar — narrow contour rail to the left of the Sidebar.
 *
 * Per CONTROL_SURFACE_LAYOUT.md the top-level navigation has 5 contours:
 *   Operational (Projects + Departments + Approvals + Proposals)
 *   Learning (University)
 *   Knowledge (KB / Wiki)
 *   Strategy (Strategy + Goals)
 *   Constitution (Mission + Principles)
 *
 * Phase 3 lands the chrome + click-plumbing only — selecting a contour
 * other than "operational" routes to a TBD placeholder page. The full
 * trees come in Phase 5 (Departments), Phase 8 (University), Phase 10
 * (Constitution) etc.
 *
 * Monochrome rules (ADR-057): no coloured fill, no saturated chrome.
 * Selection is indicated by a 2px hairline accent on the left edge and a
 * subtle bg wash; the icon itself stays in `text-muted-foreground`
 * unless hovered or selected (then `text-foreground`).
 */
import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  Compass,
  FolderKanban,
  FolderTree,
  Library,
  Network,
  Scale,
  Sparkles,
} from 'lucide-react';

import { cn } from '../../lib/utils';

export type ActivityContour =
  | 'operational'
  | 'files'
  | 'architect'
  | 'departments'
  | 'learning'
  | 'knowledge'
  | 'strategy'
  | 'constitution';

interface ContourSpec {
  key: ActivityContour;
  label: string;
  icon: LucideIcon;
  /** When true, the icon is the only enabled contour today. */
  enabled: boolean;
}

const CONTOURS: ContourSpec[] = [
  { key: 'operational', label: 'Operational', icon: FolderKanban, enabled: true },
  { key: 'files', label: 'Files', icon: FolderTree, enabled: true },
  { key: 'architect', label: 'Architect', icon: Sparkles, enabled: true },
  { key: 'departments', label: 'Departments', icon: Network, enabled: true },
  { key: 'learning', label: 'University', icon: BookOpen, enabled: true },
  { key: 'knowledge', label: 'Knowledge', icon: Library, enabled: false },
  { key: 'strategy', label: 'Strategy', icon: Compass, enabled: true },
  { key: 'constitution', label: 'Constitution', icon: Scale, enabled: true },
];

interface ActivityBarProps {
  active: ActivityContour;
  onSelect: (contour: ActivityContour) => void;
}

export function ActivityBar({ active, onSelect }: ActivityBarProps) {
  return (
    <nav
      aria-label="Workspace contours"
      className="flex w-12 shrink-0 flex-col items-center border-r border-border bg-muted/20 py-2"
    >
      {CONTOURS.map((c) => {
        const Icon = c.icon;
        const isActive = c.key === active;
        return (
          <button
            key={c.key}
            type="button"
            aria-label={c.label}
            aria-pressed={isActive}
            disabled={!c.enabled}
            onClick={() => c.enabled && onSelect(c.key)}
            title={c.enabled ? c.label : `${c.label} — coming soon`}
            className={cn(
              'relative my-0.5 flex h-8 w-8 items-center justify-center rounded transition-colors',
              isActive
                ? 'bg-foreground/5 text-foreground'
                : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              !c.enabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
            )}
          >
            {isActive && (
              <span
                aria-hidden
                className="absolute left-[-6px] top-1.5 bottom-1.5 w-[2px] rounded-r bg-foreground"
              />
            )}
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        );
      })}
    </nav>
  );
}
